/**
 * ③ MATERIALITY INTELLIGENCE + FRAMEWORKS — LAYERED DRILL-DOWN
 * UC-17: TaxonomyAlignment, UC-22: CBAMEstimator, UC-16: OmnibusScope, UC-30: JurisdictionOverlap
 */
import { useState, useEffect } from "react";
import { C, api, post, Btn, Card, Label, Val, Input, Badge, Err, MetricBar,
         Breadcrumb, DrillRow, DetailPanel, TabBar, HistoryTab, EmptyState, KPICard } from "./shared";

// ═══ UC-17: TAXONOMY ALIGNMENT — activity drill ═══
export function TaxonomyAlignment() {
  const [nace, setNace] = useState("C24,D35.11");
  const [result, setResult] = useState(null);
  const [loading, setLoading] = useState(false);
  const [sel, setSel] = useState(null);
  const [actDetail, setActDetail] = useState(null);
  const [tab, setTab] = useState("Overview");

  const check = async () => {
    setLoading(true); setSel(null);
    try { setResult(await post("/taxonomy/check-eligibility", { nace_codes: nace.split(",").map(s => s.trim()) })); }
    catch (e) {} setLoading(false);
  };

  const drillActivity = async (a) => {
    setSel(a); setTab("Overview"); setActDetail(null);
    if (a.activity_id) try { setActDetail(await api(`/taxonomy/activities/${a.activity_id}`)); } catch (e) {}
  };

  const acts = result?.eligible_activities || [];
  return (<div data-testid="taxonomy-alignment-panel">
    <Breadcrumb path={[{ label: "EU Taxonomy", onClick: () => setSel(null) }, ...(sel ? [{ label: sel.name || sel.activity }] : [])]} />

    {!sel && <>
      <div style={{ display: "flex", gap: 6, marginBottom: 10, alignItems: "flex-end" }}>
        <div style={{ flex: 1 }}><Label>NACE Codes</Label><Input value={nace} onChange={setNace} placeholder="C24, D35.11" /></div>
        <Btn onClick={check} color={C.teal} disabled={loading}>{loading ? "..." : "Check Eligibility"}</Btn>
      </div>
      {result && <>
        <div style={{ display: "flex", gap: 8, marginBottom: 12 }}>
          <KPICard label="Eligible Activities" value={result.total_eligible || acts.length} color={C.teal} />
          <KPICard label="NACE Codes" value={result.nace_codes?.length || 0} color={C.blue} />
        </div>
        <Label>Eligible Activities — click for TSC & DNSH detail</Label>
        {acts.map((a, i) => (
          <DrillRow key={i} onClick={() => drillActivity(a)} cols={[
            <span style={{ fontWeight: 600 }}>{a.name || a.activity}</span>,
            a.sector || "—",
            <Badge text={a.type || a.category || "eligible"} color={a.type === "enabling" ? C.blue : a.type === "transitional" ? C.amber : C.teal} />
          ]} />
        ))}
        {acts.length === 0 && <EmptyState message="No eligible activities found for these NACE codes" />}
      </>}
    </>}

    {sel && (
      <DetailPanel visible onClose={() => setSel(null)} title={sel.name || sel.activity} color={C.teal}>
        <TabBar tabs={["Overview", "TSC Criteria", "DNSH", "History"]} active={tab} onChange={setTab} />
        {tab === "Overview" && <div>
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: 8 }}>
            <div><Label>Sector</Label><Val>{sel.sector || "—"}</Val></div>
            <div><Label>Type</Label><Badge text={sel.type || "eligible"} color={C.teal} /></div>
            <div><Label>NACE</Label><Val>{sel.nace_code || sel.nace || "—"}</Val></div>
          </div>
          {sel.description && <div style={{ marginTop: 8 }}><Label>Description</Label><div style={{ fontSize: 11, lineHeight: 1.5 }}>{sel.description}</div></div>}
        </div>}
        {tab === "TSC Criteria" && <div>
          {(actDetail?.tsc || sel.tsc_criteria || []).length > 0 ?
            (actDetail?.tsc || sel.tsc_criteria || []).map((t, i) => (
              <div key={i} style={{ padding: "6px 0", borderBottom: `1px solid ${C.border}15`, fontSize: 11 }}>
                <span style={{ fontWeight: 600 }}>{t.objective || t.name}: </span>
                <span style={{ color: C.muted }}>{t.threshold || t.criteria || "See Delegated Act"}</span>
                <span style={{ marginLeft: 8 }}><Badge text={t.met ? "MET" : "CHECK"} color={t.met ? C.emerald : C.amber} /></span>
              </div>
            )) :
            <div style={{ fontSize: 11, color: C.muted }}>TSC thresholds vary by activity. Check Delegated Acts Annex I/II for specific metrics.</div>
          }
        </div>}
        {tab === "DNSH" && <div>
          {["Climate Mitigation", "Climate Adaptation", "Water", "Circular Economy", "Pollution", "Biodiversity"].map((obj, i) => (
            <div key={i} style={{ display: "flex", justifyContent: "space-between", padding: "6px 0", borderBottom: `1px solid ${C.border}15`, fontSize: 11 }}>
              <span>{obj}</span>
              <Badge text="ASSESS" color={C.amber} />
            </div>
          ))}
          <div style={{ marginTop: 8, fontSize: 10, color: C.muted }}>Each DNSH objective requires evidence that the activity does not significantly harm that objective.</div>
        </div>}
        {tab === "History" && <HistoryTab resourceId={sel.activity_id || "taxonomy"} />}
      </DetailPanel>
    )}
  </div>);
}

// ═══ UC-22: CBAM ESTIMATOR — with factor drill ═══
export function CBAMEstimator() {
  const [query, setQuery] = useState("steel");
  const [results, setResults] = useState([]);
  const [sel, setSel] = useState(null);

  const search = async () => {
    try { setResults((await api(`/data/search/ef?q=${query}+TR&scope=1`)).results || []); } catch (e) {}
  };

  return (<div data-testid="cbam-estimator-panel">
    <Breadcrumb path={[{ label: "CBAM", onClick: () => setSel(null) }, ...(sel ? [{ label: sel.activity }] : [])]} />
    {!sel && <>
      <div style={{ display: "flex", gap: 6, marginBottom: 10 }}>
        <div style={{ flex: 1 }}><Input value={query} onChange={setQuery} placeholder="steel, aluminium, cement..." /></div>
        <Btn onClick={search} color={C.orange}>Search CBAM Factors</Btn>
      </div>
      <KPICard label="Factors Found" value={results.length} color={C.orange} />
      <div style={{ marginTop: 8 }}>
        {results.map((r, i) => (
          <DrillRow key={i} onClick={() => setSel(r)} cols={[
            <span style={{ fontWeight: 600 }}>{r.activity}</span>,
            <span style={{ color: C.orange }}>{r.value_total} {r.unit_out}/{r.unit_in}</span>,
            r.geography || "—"
          ]} />
        ))}
      </div>
    </>}
    {sel && (
      <DetailPanel visible onClose={() => setSel(null)} title={sel.activity} color={C.orange}>
        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: 8 }}>
          <div><Label>Factor</Label><Val color={C.orange}>{sel.value_total}</Val></div>
          <div><Label>Unit</Label><Val>{sel.unit_out}/{sel.unit_in}</Val></div>
          <div><Label>Source</Label><Val>{sel.source} {sel.year}</Val></div>
        </div>
        <div style={{ marginTop: 8, fontSize: 10, color: C.muted }}>CBAM certificates required = embedded emissions x factor. Report via CBAM Transitional Registry.</div>
      </DetailPanel>
    )}
  </div>);
}

// ═══ UC-16: OMNIBUS SCOPE — with threshold detail ═══
export function OmnibusScope() {
  const [result, setResult] = useState(null);
  const [loading, setLoading] = useState(false);

  const check = async () => { setLoading(true); try { setResult(await post("/regulatory/omnibus/check-scope", {})); } catch (e) {} setLoading(false); };
  useEffect(() => { check(); }, []);

  return (<div data-testid="omnibus-scope-panel">
    <Label>Omnibus I Scope Check</Label>
    {result && <>
      <div style={{ display: "flex", gap: 8, marginBottom: 12 }}>
        <KPICard label="Entity" value={result.entity || "Stahl AG"} color={C.blue} />
        <KPICard label="Employees" value={(result.employee_count || 0).toLocaleString()} color={result.employee_count > 1000 ? C.emerald : C.amber} />
        <KPICard label="Revenue" value={`€${((result.revenue_eur || 0) / 1e9).toFixed(1)}B`} color={C.blue} />
        <KPICard label="In Scope?" value={result.in_scope ? "YES" : "NO"} color={result.in_scope ? C.red : C.emerald} />
      </div>
      {result.thresholds && <Card>
        <Label>Omnibus I Thresholds</Label>
        {Object.entries(result.thresholds || {}).map(([k, v]) => (
          <div key={k} style={{ display: "flex", justifyContent: "space-between", padding: "4px 0", fontSize: 11, borderBottom: `1px solid ${C.border}15` }}>
            <span>{k}</span><span style={{ color: C.cyan }}>{typeof v === "number" ? v.toLocaleString() : v}</span>
          </div>
        ))}
      </Card>}
      {result.advisory && <div style={{ marginTop: 8, fontSize: 11, color: C.muted, lineHeight: 1.5 }}>{result.advisory}</div>}
    </>}
    {!result && <Btn onClick={check} color={C.teal} disabled={loading}>{loading ? "..." : "Check Scope"}</Btn>}
  </div>);
}

// ═══ UC-30: JURISDICTION OVERLAP — with datapoint drill ═══
export function JurisdictionOverlap() {
  const [result, setResult] = useState(null);
  const [loading, setLoading] = useState(false);
  const [sel, setSel] = useState(null);

  const check = async () => {
    setLoading(true);
    try { setResult(await post("/compliance/overlap", { frameworks: ["CSRD/ESRS", "ISSB", "CDP"] })); }
    catch (e) {} setLoading(false);
  };
  useEffect(() => { check(); }, []);

  return (<div data-testid="jurisdiction-overlap-panel">
    <Breadcrumb path={[{ label: "Framework Overlap", onClick: () => setSel(null) }, ...(sel ? [{ label: sel }] : [])]} />

    {!sel && <>
      {result && <>
        <div style={{ display: "flex", gap: 8, marginBottom: 12 }}>
          <KPICard label="Frameworks" value={result.frameworks || 0} color={C.blue} />
          <KPICard label="Total Unique DPs" value={result.total_unique_datapoints || 0} color={C.blue} />
          <KPICard label="Shared DPs" value={result.shared_datapoints || 0} color={C.emerald}
            onClick={() => setSel("shared")} sub="Click for shared list" />
          <KPICard label="Overlap %" value={`${(result.overlap_pct || 0).toFixed(1)}%`} color={C.teal}
            sub="Effort saved by entering once" />
        </div>

        {(result.framework_details || []).map((fw, i) => (
          <DrillRow key={i} onClick={() => setSel(fw.framework || fw.name)} cols={[
            <span style={{ fontWeight: 600 }}>{fw.framework || fw.name}</span>,
            <span>Total: {fw.total_datapoints || 0}</span>,
            <span style={{ color: C.emerald }}>Shared: {fw.shared || 0}</span>,
            <span style={{ color: C.amber }}>Unique: {fw.unique || 0}</span>
          ]} />
        ))}

        {(result.shared_datapoints_list || []).length > 0 && <>
          <Label>Shared Datapoints (collect once, reuse across all)</Label>
          {result.shared_datapoints_list.map((dp, i) => (
            <div key={i} style={{ padding: "4px 0", borderBottom: `1px solid ${C.border}15`, fontSize: 10 }}>
              <span style={{ color: C.emerald, marginRight: 6 }}>OK</span>{dp}
            </div>
          ))}
        </>}
      </>}
      {!result && <Btn onClick={check} color={C.teal} disabled={loading}>{loading ? "..." : "Analyze Overlap"}</Btn>}
    </>}

    {sel && (
      <DetailPanel visible onClose={() => setSel(null)} title={`${sel} Datapoints`} color={C.teal}>
        <div style={{ fontSize: 11, color: C.muted, lineHeight: 1.5 }}>
          {sel === "shared" ? "These datapoints are required by multiple frameworks. Upload the evidence once — DisclosureOS maps it to all frameworks automatically." :
            `Framework-specific datapoints for ${sel}. Some overlap with other frameworks (shown in green), others are unique to ${sel} (shown in amber).`}
        </div>
      </DetailPanel>
    )}
  </div>);
}
